import React, { useState } from 'react';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { InvitationService, USER_ROLES, UserRole } from '../services/invitationService';
import Button from './Button';
import Modal from './Modal';
import Alert from './Alert';

interface Organization {
  id: string;
  name: string;
}

interface InviteData {
  email: string;
  role: UserRole;
}

export interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization;
  onInviteSuccess: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  organization,
  onInviteSuccess
}) => {
  const [inviteData, setInviteData] = useState<InviteData>({
    email: '',
    role: USER_ROLES.MEMBER
  });
  const [error, setError] = useState<string>('');

  const handleInviteDataChange =
    (field: keyof InviteData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      setInviteData({ ...inviteData, [field]: e.target.value });
    };

  const handleInviteUser = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    try {
      const inviteResult = await InvitationService.inviteUser({
        email: inviteData.email,
        organizationId: organization.id,
        role: inviteData.role
      });

      if (inviteResult.success) {
        setInviteData({ email: '', role: USER_ROLES.MEMBER });
        onInviteSuccess();
        onClose();
      } else {
        setError(inviteResult.message || 'Erro ao enviar convite');
      }
    } catch (error) {
      setError('Ocorreu um erro ao processar o convite');
      console.error('Invitation error:', error);
    }
  };

  const modalDescription = `Convide um usuário para se juntar à organização: ${organization.name}`;

  const footerContent = (
    <div className="flex justify-end space-x-3">
      <Button type="button" onClick={onClose} variant="secondary">
        Cancelar
      </Button>
      <Button type="submit" form="invite-user-form" leftIcon={faUserPlus} variant="success">
        Enviar Convite
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Convidar Usuário"
      description={modalDescription}
      footer={footerContent}
    >
      <form id="invite-user-form" onSubmit={handleInviteUser} className="space-y-4">
        {error && <Alert variant="error" message={error} />}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email do usuário</label>
          <input
            type="email"
            required
            value={inviteData.email}
            onChange={handleInviteDataChange('email')}
            placeholder="usuario@exemplo.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Função na organização
          </label>
          <select
            value={inviteData.role}
            onChange={handleInviteDataChange('role')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {InvitationService.getRoleOptions()
              .filter(role => role.value !== 'saasadmin')
              .map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Membros têm acesso básico, coordenadores têm controle total da organização.
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default InviteUserModal;
